import { useMemo, useState } from 'react';
import { EmployeeDialog } from './EmployeeDialog';
import { CrewDialog } from './CrewDialog';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { EmptyState } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import { useCrews, useEmployees, employeeMutations, crewMutations } from '../../lib/queries';
import { EMPLOYMENT_LABELS } from '../../lib/labels';
import { formatDate } from '../../lib/dates';
import { ApiError } from '../../lib/api';
import type { Crew, Employee, SessionUser } from '../../lib/types';
import './mitarbeiter.css';

export function MitarbeiterView({ user }: { user: SessionUser }) {
  const canEdit = user.role !== 'betrachter';
  const employees = useEmployees();
  const crews = useCrews();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [crewFilter, setCrewFilter] = useState<string>('alle');
  const [showInactive, setShowInactive] = useState(false);

  const [editing, setEditing] = useState<Employee | null | undefined>(undefined);
  const [editingCrew, setEditingCrew] = useState<Crew | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<Employee | null>(null);
  const [deletingCrew, setDeletingCrew] = useState<Crew | null>(null);

  const removeEmployee = employeeMutations.useDelete();
  const removeCrew = crewMutations.useDelete();

  const crewsById = useMemo(
    () => new Map((crews.data ?? []).map((crew) => [crew.id, crew])),
    [crews.data],
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (employees.data ?? []).filter((employee) => {
      if (!showInactive && !employee.active) return false;
      if (crewFilter !== 'alle') {
        const wanted = crewFilter === 'ohne' ? null : Number(crewFilter);
        if (employee.crewId !== wanted) return false;
      }
      if (!term) return true;
      return `${employee.displayName} ${employee.role} ${employee.shortName}`
        .toLowerCase()
        .includes(term);
    });
  }, [employees.data, search, crewFilter, showInactive]);

  const inactiveCount = (employees.data ?? []).filter((employee) => !employee.active).length;

  if (employees.isLoading || crews.isLoading) {
    return (
      <div className="loading">
        <span className="spinner" aria-hidden="true" />
        Mitarbeiterdaten werden geladen …
      </div>
    );
  }

  return (
    <div className="view">
      <header className="view-head">
        <div className="view-title">
          <span className="label">Stammdaten</span>
          <h1>Mitarbeiter</h1>
          <p className="view-lead">
            {visible.length} von {employees.data?.length ?? 0} angezeigt
            {inactiveCount > 0 ? ` · ${inactiveCount} inaktiv` : ''}
          </p>
        </div>

        {canEdit ? (
          <div className="row row-wrap">
            <button type="button" className="btn" onClick={() => setEditingCrew(null)}>
              Kolonne anlegen
            </button>
            <button type="button" className="btn btn-signal" onClick={() => setEditing(null)}>
              Mitarbeiter anlegen
            </button>
          </div>
        ) : null}
      </header>

      <CrewStrip
        crews={crews.data ?? []}
        employees={employees.data ?? []}
        canEdit={canEdit}
        onEdit={setEditingCrew}
        onDelete={setDeletingCrew}
      />

      <section className="card">
        <div className="filter-bar">
          <input
            type="search"
            className="input filter-search"
            placeholder="Nach Name oder Funktion suchen …"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Mitarbeiter suchen"
          />
          <select
            className="select filter-select"
            value={crewFilter}
            onChange={(event) => setCrewFilter(event.target.value)}
            aria-label="Nach Kolonne filtern"
          >
            <option value="alle">Alle Kolonnen</option>
            {(crews.data ?? []).map((crew) => (
              <option key={crew.id} value={crew.id}>
                {crew.name}
              </option>
            ))}
            <option value="ohne">Ohne Kolonne</option>
          </select>
          <label className="check">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(event) => setShowInactive(event.target.checked)}
            />
            Inaktive anzeigen
          </label>
        </div>

        {visible.length === 0 ? (
          <EmptyState
            title="Keine Mitarbeiter gefunden"
            description={
              (employees.data?.length ?? 0) === 0
                ? 'Lege den ersten Mitarbeiter an, damit im Schichtplan geplant werden kann.'
                : 'Für diese Filter gibt es keine Treffer.'
            }
            {...(canEdit && (employees.data?.length ?? 0) === 0
              ? {
                  action: (
                    <button type="button" className="btn btn-signal" onClick={() => setEditing(null)}>
                      Mitarbeiter anlegen
                    </button>
                  ),
                }
              : {})}
          />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Kürzel</th>
                  <th>Kolonne</th>
                  <th>Funktion</th>
                  <th>Beschäftigung</th>
                  <th className="align-right">Urlaub</th>
                  <th className="align-right">Std./Woche</th>
                  <th>Eintritt</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((employee) => {
                  const crew = employee.crewId ? crewsById.get(employee.crewId) : undefined;
                  return (
                    <tr key={employee.id} className={employee.active ? '' : 'row-inactive'}>
                      <td>
                        <div className="cell-name">
                          <strong>{employee.displayName}</strong>
                          {!employee.active ? <span className="badge">Inaktiv</span> : null}
                        </div>
                        {employee.phone ? <span className="hint">{employee.phone}</span> : null}
                      </td>
                      <td className="numeric">{employee.shortName}</td>
                      <td>
                        {crew ? (
                          <span className="row" style={{ gap: 'var(--space-2)' }}>
                            <span className="swatch" style={{ background: crew.color }} />
                            {crew.name}
                          </span>
                        ) : (
                          <span className="faint">—</span>
                        )}
                      </td>
                      <td>{employee.role || <span className="faint">—</span>}</td>
                      <td>{EMPLOYMENT_LABELS[employee.employmentType]}</td>
                      <td className="align-right numeric">{employee.vacationEntitlement}</td>
                      <td className="align-right numeric">{employee.weeklyHours}</td>
                      <td className="numeric">
                        {employee.entryDate ? formatDate(employee.entryDate) : <span className="faint">—</span>}
                      </td>
                      <td className="align-right">
                        {canEdit ? (
                          <div className="row-actions">
                            <button
                              type="button"
                              className="btn btn-small"
                              onClick={() => setEditing(employee)}
                            >
                              Bearbeiten
                            </button>
                            <button
                              type="button"
                              className="btn btn-small btn-danger"
                              onClick={() => setDeleting(employee)}
                            >
                              Löschen
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing !== undefined ? (
        <EmployeeDialog
          employee={editing}
          crews={crews.data ?? []}
          onClose={() => setEditing(undefined)}
        />
      ) : null}

      {editingCrew !== undefined ? (
        <CrewDialog crew={editingCrew} onClose={() => setEditingCrew(undefined)} />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          title="Mitarbeiter löschen"
          message={`"${deleting.displayName}" wirklich löschen?`}
          detail="Der Mitarbeiter verschwindet aus dem Plan und aus allen Listen. Bereits vergangene Einteilungen bleiben zur Nachvollziehbarkeit erhalten; künftige Einteilungen werden entfernt."
          confirmLabel="Löschen"
          busy={removeEmployee.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() =>
            removeEmployee.mutate(
              { id: deleting.id },
              {
                onSuccess: () => {
                  toast.success(`"${deleting.displayName}" wurde gelöscht.`);
                  setDeleting(null);
                },
                onError: (error) => {
                  toast.error(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen.');
                  setDeleting(null);
                },
              },
            )
          }
        />
      ) : null}

      {deletingCrew ? (
        <ConfirmDialog
          title="Kolonne löschen"
          message={`Kolonne "${deletingCrew.name}" wirklich löschen?`}
          detail="Die Mitarbeiter bleiben erhalten — sie stehen danach ohne Kolonne. Bestehende Einteilungen ändern sich nicht."
          confirmLabel="Löschen"
          busy={removeCrew.isPending}
          onCancel={() => setDeletingCrew(null)}
          onConfirm={() =>
            removeCrew.mutate(
              { id: deletingCrew.id },
              {
                onSuccess: () => {
                  toast.success(`Kolonne "${deletingCrew.name}" wurde gelöscht.`);
                  setDeletingCrew(null);
                },
                onError: (error) => {
                  toast.error(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen.');
                  setDeletingCrew(null);
                },
              },
            )
          }
        />
      ) : null}
    </div>
  );
}

/** Kolonnen als Kartenreihe — Besetzungsstärke auf einen Blick. */
function CrewStrip({
  crews,
  employees,
  canEdit,
  onEdit,
  onDelete,
}: {
  crews: Crew[];
  employees: Employee[];
  canEdit: boolean;
  onEdit: (crew: Crew) => void;
  onDelete: (crew: Crew) => void;
}) {
  if (crews.length === 0) return null;

  const withoutCrew = employees.filter((employee) => employee.crewId === null && employee.active).length;

  return (
    <section aria-label="Kolonnen">
      <span className="label" style={{ marginBottom: 'var(--space-2)' }}>
        Kolonnen
      </span>
      <div className="crew-strip">
        {crews.map((crew) => {
          const count = employees.filter(
            (employee) => employee.crewId === crew.id && employee.active,
          ).length;
          return (
            <article key={crew.id} className="crew-card" style={{ '--crew': crew.color } as never}>
              <div className="crew-card-head">
                <h3>{crew.name}</h3>
                {!crew.active ? <span className="badge">Inaktiv</span> : null}
              </div>
              <p className="crew-count numeric">
                {count} <span>{count === 1 ? 'Mitarbeiter' : 'Mitarbeiter'}</span>
              </p>
              {canEdit ? (
                <div className="crew-card-actions">
                  <button type="button" className="btn btn-ghost btn-small" onClick={() => onEdit(crew)}>
                    Bearbeiten
                  </button>
                  <button type="button" className="btn btn-ghost btn-small" onClick={() => onDelete(crew)}>
                    Löschen
                  </button>
                </div>
              ) : null}
            </article>
          );
        })}

        {withoutCrew > 0 ? (
          <article className="crew-card crew-card-plain">
            <div className="crew-card-head">
              <h3>Ohne Kolonne</h3>
            </div>
            <p className="crew-count numeric">
              {withoutCrew} <span>Mitarbeiter</span>
            </p>
          </article>
        ) : null}
      </div>
    </section>
  );
}
